import React, {useEffect, useState} from "react";
import {useTheme} from "connectui/theme/ThemeProvider";
import ICONS from "connectui/icons/iconMap";
import ContextMenu from "connectui/component/input/context/ContextMenu";
import SnoozeModal from "../modal/SnoozeModal";
import ButtonIconText from "connectui/component/input/button/ButtonIconText";
import SvgIcon from "connectui/component/display/SvgIcon";
import FlexContainer from "connectui/component/layout/FlexContainer";
import {format, isSameDay} from "date-fns";
import Text from "connectui/component/display/Text";
import {useTranslation} from "react-i18next";

const SnoozeButton = ({
  menuOptions = {menuEnabled: false, menuAnchorEl: null, closeMenu: () => null},
  handleSnooze,
}) => {
  const {theme} = useTheme();
  const {t} = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [snoozeModalOpen, setSnoozeModalOpen] = useState(false);
  const now = new Date();
  const hour1 = new Date(Date.now() + 3600000);
  const hour4 = new Date(Date.now() + 4 * 3600000);
  const tomorrowMorning = new Date(now);
  tomorrowMorning.setDate(now.getDate() + 1);
  tomorrowMorning.setHours(8, 0, 0, 0); // 8:00 AM tmrw;

  const [menuItems, setMenuItems] = useState([
    {
      label: t("inbox.conversations.header.snooze.options.one-hour.label"),
      subLabel: isSameDay(now, hour1)
        ? t("inbox.conversations.header.snooze.options.one-hour.today", {
            time: format(hour1, "p"),
          })
        : t("inbox.conversations.header.snooze.options.one-hour.other", {
            date: format(hour1, "MMM d"),
            time: format(hour1, "p"),
          }),
      onClick: () => {
        handleSnooze(hour1.getTime());
        setAnchorEl(null);
        menuOptions.closeMenu();
      },
    },
    {
      label: t("inbox.conversations.header.snooze.options.four-hours.label"),
      subLabel: isSameDay(now, hour4)
        ? t("inbox.conversations.header.snooze.options.four-hours.today", {
            time: format(hour4, "p"),
          })
        : t("inbox.conversations.header.snooze.options.four-hours.other", {
            date: format(hour4, "MMM d"),
            time: format(hour4, "p"),
          }),
      onClick: () => {
        handleSnooze(hour4.getTime());
        setAnchorEl(null);
        menuOptions.closeMenu();
      },
    },
    {
      label: t("inbox.conversations.header.snooze.options.tomorrow.label"),
      subLabel: t("inbox.conversations.header.snooze.options.tomorrow.date", {
        date: format(tomorrowMorning, "MMM d"),
        time: format(tomorrowMorning, "p"),
      }),
      onClick: () => {
        handleSnooze(tomorrowMorning.getTime());
        setAnchorEl(null);
        menuOptions.closeMenu();
      },
    },
    {
      label: t("inbox.conversations.header.snooze.options.custom"),
      icon: ICONS["calendar"],
      onClick: () => {
        setSnoozeModalOpen(true);
        setAnchorEl(null);
        menuOptions.closeMenu();
      },
    },
  ]);

  useEffect(() => {
    if (menuOptions.menuAnchorEl && menuItems.length === 4) {
      setMenuItems((prev) => [
        {
          Component: ({label}) => (
            <FlexContainer
              alignItems="center"
              gap={theme.spacing(2)}
              sx={{
                borderBottom: `1px solid ${theme.colors.border}`,
                padding: theme.spacing(1, 2),
                cursor: "pointer",
              }}
              onClick={() => menuOptions.closeMenu(true)}
            >
              <SvgIcon
                component={ICONS["chevron-left"]}
                sx={{
                  width: "20px",
                  height: "20px",
                  padding: theme.spacing(1),
                  color: theme.colors.textTertiary,
                }}
              />
              <Text variant="textMD" weight="medium" color="textTertiary">
                {t("inbox.conversations.header.snooze.button.back")}
              </Text>
            </FlexContainer>
          ),
          label: t("inbox.conversations.header.snooze.button.label"),
        },
        ...prev,
      ]);
    } else if (!menuOptions.menuAnchorEl && menuItems.length > 4) {
      setMenuItems((prev) => prev.slice(1));
    }
  }, [menuOptions, t]);

  return (
    <>
      <ContextMenu
        menuItems={menuItems}
        modalProps={{
          modalPosition: {horizontal: "end"},
          anchorOrigin: {horizontal: "end"},
        }}
        open={!!anchorEl || !!menuOptions.menuAnchorEl}
        onClose={() => {
          setAnchorEl(null);
          menuOptions.closeMenu();
        }}
        anchorEl={anchorEl ?? menuOptions.menuAnchorEl}
      />
      <SnoozeModal
        isOpen={snoozeModalOpen}
        onClose={(value) => {
          setSnoozeModalOpen(false);
          if (value) handleSnooze(value);
        }}
      />
      {!menuOptions.menuEnabled && (
        <ButtonIconText
          label={
            <FlexContainer alignItems="center" gap={theme.spacing(1)}>
              {t("inbox.conversations.header.snooze.button.label")}
              <SvgIcon
                component={ICONS[!!anchorEl ? "chevron-up" : "chevron-down"]}
                sx={{
                  width: "8px",
                  height: "8px",
                  color: theme.colors.textTertiary,
                }}
              />
            </FlexContainer>
          }
          weight="medium"
          color="gray700"
          typographyVariant="textSM"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{
            backgroundColor: theme.colors.gray100,
            borderRadius: theme.spacing(4),
            padding: theme.spacing(1, 3),
            "&:hover": {backgroundColor: theme.colors.gray200},
          }}
          icon={
            <SvgIcon
              component={ICONS["alarm-snooze"]}
              sx={{
                marginRight: theme.spacing(1),
                width: "14px",
                height: "14px",
                color: theme.colors.textSecondary,
              }}
            />
          }
        />
      )}
    </>
  );
};

export default SnoozeButton;
